import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { DialogTitle } from '@openx/components-core/lib/Dialog/DialogTitle/DialogTitle';
import { noop } from '@openx/utils/noop';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { actionsSx, fieldWrapperSx, headingSx, subtitleSx, textFieldInputSx, titleSx } from './styles';

type FeedbackModalProps = {
  onSubmit: (feedbackText: string) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export const FeedbackModal = ({ onSubmit, onCancel, isSubmitting = false }: FeedbackModalProps) => {
  const { t } = useTranslation();
  const [feedbackText, setFeedbackText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const sendFeedbackHandler = useCallback(async () => {
    try {
      await onSubmit(feedbackText);
    } catch {
      noop();
    }
  }, [onSubmit, feedbackText]);

  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  return (
    <Dialog
      ref={containerRef}
      onClose={onCancel}
      open={true}
      sx={{
        zIndex: 1402,
      }}
    >
      <DialogTitle data-test="modal-title" onClose={onCancel}>
        <Stack direction="column" sx={headingSx}>
          <Typography variant="h2" sx={titleSx}>
            {t('What was the problem?')}
          </Typography>
          <Typography variant="body1" sx={subtitleSx}>
            {t('Your feedback will help make our AI even better.')}
          </Typography>
        </Stack>
      </DialogTitle>

      <Divider sx={{ width: '100%', border: 'none', borderTop: theme => `1px solid ${theme.palette.divider}` }} />

      <DialogContent>
        <Box sx={fieldWrapperSx}>
          <TextField
            multiline
            fullWidth
            minRows={8}
            maxRows={8}
            placeholder={t("Tell us why you didn't like this response.")}
            aria-label={t("Tell us why you didn't like this response.")}
            value={feedbackText}
            onChange={event => setFeedbackText(event.target.value)}
            slotProps={{
              input: {
                sx: textFieldInputSx,
              },
            }}
          />
        </Box>
      </DialogContent>

      <Divider sx={{ width: '100%', border: 'none', borderTop: theme => `1px solid ${theme.palette.divider}` }} />

      <DialogActions>
        <Stack direction="row" sx={actionsSx}>
          <Button variant="text" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button
            loading={isSubmitting}
            disabled={isSubmitting}
            variant="contained"
            color="primary"
            onClick={sendFeedbackHandler}
          >
            {t('Send feedback')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
